import produce from 'immer';
import { chunk, isEqual, isNil } from 'lodash';

import {
  ADD_EMPTY_ENTITY_VALUE_ACTION,
  CHANGE_MODAL_TAB_SECTION_ACTION,
  CHANGE_PAGE_ACTION,
  DELETE_ENTITY_ACTION,
  GO_BACK_TO_INITIAL_UPLOAD_STEP_ACTION,
  GO_TO_SUCCESSFUL_UPLOAD_STEP_ACTION,
  REMOVE_UPLOADED_FILE,
  TOGGLE_AGREE_TO_CREATE_NEW_COLLECTION,
  TOGGLE_EXPLANATION_BANNER_ACTION,
  TOGGLE_MODAL_ACTION,
  UPDATE_ENTITIES_ACTION,
  UPDATE_ENTITY_VALUE_ACTION,
  UPDATE_GENERAL_COUNTRIES_LIST_ACTION
} from '../constants/actions';
import {
  CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER,
  CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER,
  ENTITIES_PER_PAGE
} from '../constants/common';
import { CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY } from '../constants/entity';

import { createReducer } from 'utils/redux';
import { getEntityIndex } from '../utils/entities';

import { CreateNewProfilesEntity, CreateNewProfilesProfilesState } from '../types/CreateNewProfiles.type';
import { UpdateEntityValue } from '../types/general';

const initialState: CreateNewProfilesProfilesState = {
  activeModal: null,
  createEntitiesModal: {
    type: CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER,
    entities: [CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY],
    isExplanationBannerCollapsed: true,
    page: 1

  },
  openedOn: 'custom-collection',
  confirmationModal: null,
  countries: []
};

export default createReducer<CreateNewProfilesProfilesState, any>('createNewProfiles', initialState, {
  [TOGGLE_MODAL_ACTION]: (state, { activeModal }) => produce(
    state, draft => {
      // eslint-disable-next-line no-param-reassign
      draft.activeModal = activeModal?.type ?? null;
      // eslint-disable-next-line no-param-reassign
      draft.openedOn = activeModal?.openedOn ?? state.openedOn;

      if (activeModal?.type === 'add-entities') {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal = {
          type: CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER,
          entities: [CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY],
          isExplanationBannerCollapsed: true,
          page: 1
        };
      }

      if (activeModal?.type === 'confirmation') {
        // eslint-disable-next-line no-param-reassign
        draft.confirmationModal = {
          wantToCreateNew: false
        };
      }
    }),
  [CHANGE_MODAL_TAB_SECTION_ACTION]: (state, { tab }) => produce(
    state, draft => {
      if (tab == CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal = {
          type: tab,
          entities: [CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY],
          isExplanationBannerCollapsed: true,
          page: 1
        };

        return;
      }

      if (tab === CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER) {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal = {
          type: tab,
          data: null
        };

        return;
      }

      throw new Error(`Impossible tab identifier: ${tab}`);
    }),
  [UPDATE_GENERAL_COUNTRIES_LIST_ACTION]: (state, { countries }) => produce(
    state, draft => {
      // eslint-disable-next-line no-param-reassign
      draft.countries = countries;
    }
  ),
  [UPDATE_ENTITY_VALUE_ACTION]: (state, { value }: { value: UpdateEntityValue }) => produce(
    state, draft => {
      let entity: CreateNewProfilesEntity | null = null;

      if (
        draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER
      ) {
        const adjustedIndex = getEntityIndex(
          value.index, draft.createEntitiesModal.page, ENTITIES_PER_PAGE
        );

        entity = draft.createEntitiesModal.entities[adjustedIndex];
      } else if (draft.createEntitiesModal.data?.type === 'successful-upload') {
        const adjustedIndex = getEntityIndex(
          value.index, draft.createEntitiesModal.data.page, ENTITIES_PER_PAGE
        );

        entity = draft.createEntitiesModal.data.entities[adjustedIndex];
      }

      if (isNil(entity)) {
        throw new Error('Entity not found!');
      }

      switch (value.type) {
        case 'artist-name':
          entity.artistName = value.name;
          entity.invalidFields = (entity.invalidFields ?? []).filter(
            e => e !== 'name'
          );
          break;
        case 'channel-urls':
          if (!isEqual(entity.channelUrls, value.urls)) {
            entity.channelUrls = value.urls;

            if (entity.channelUrls.length > 0) {
              entity.invalidFields = (entity.invalidFields ?? []).filter(
                e => e !== 'urls'
              );
            }
          }
          break;
        case 'gender':
          entity.gender = value.gender;
          break;
        case 'locations':
          entity.locations = value.locations;
          break;
        case 'email':
          entity.email = value.email;
          entity.invalidFields = (entity.invalidFields ?? []).filter(
            e => e !== 'email'
          );
          break;
        default:
          break;
      }
    }
  ),
  [ADD_EMPTY_ENTITY_VALUE_ACTION]: state => produce(
    state, draft => {
      if (draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
        draft.createEntitiesModal.entities.push(CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY);
      } else if (draft.createEntitiesModal.data?.type === 'successful-upload') {
        draft.createEntitiesModal.data.entities.push(CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY);
      }
    }
  ),
  [DELETE_ENTITY_ACTION]: (state, { index }) => produce(
    state, draft => {
      if (draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
        draft.createEntitiesModal.entities.splice(index, 1);
      } else if (draft.createEntitiesModal.data?.type === 'successful-upload') {
        draft.createEntitiesModal.data.entities.splice(index, 1);
      }
    }
  ),
  [TOGGLE_EXPLANATION_BANNER_ACTION]: state => produce(
    state, draft => {
      if (draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal.isExplanationBannerCollapsed = !draft.createEntitiesModal.isExplanationBannerCollapsed;
      }
    }
  ),
  [GO_BACK_TO_INITIAL_UPLOAD_STEP_ACTION]: state => produce(
    state, draft => {
      if (draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER) {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal = {
          type: CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER,
          data: null
        };
      }
    }
  ),
  [GO_TO_SUCCESSFUL_UPLOAD_STEP_ACTION]: state => produce(
    state, draft => {
      if (draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER) {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal = {
          type: CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER,
          data: {
            type: 'successful-upload',
            entities: [CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY],
            page: 1
          }
        };
      }
    }
  ),
  [UPDATE_ENTITIES_ACTION]: (state, { entities, filename }) => produce(
    state, draft => {
      if (draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER) {
        if (entities.length === 0) {
          // eslint-disable-next-line no-param-reassign
          draft.createEntitiesModal.data = {
            type: 'unsuccessful-upload',
            filename: filename ?? ''
          };
        } else {
          // eslint-disable-next-line no-param-reassign
          draft.createEntitiesModal.data = {
            type: 'successful-upload',
            entities,
            page: 1
          };
        }
      } else {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal.entities = entities;
      }
    }
  ),
  [CHANGE_PAGE_ACTION]: (state, { page }) => produce(
    state, draft => {
      if (draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
        const maxPages = chunk(draft.createEntitiesModal.entities, ENTITIES_PER_PAGE).length;

        if (page < 0 || page > maxPages) {
          throw new Error(`Impossible page number: ${page}.`);
        }

        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal.page = page;
      } else if (draft.createEntitiesModal.data?.type === 'successful-upload') {
        const maxPages = chunk(draft.createEntitiesModal.data.entities, ENTITIES_PER_PAGE).length;

        if (page < 0 || page > maxPages) {
          throw new Error(`Impossible page number: ${page}.`);
        }

        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal.data.page = page;
      }
    }
  ),
  [TOGGLE_AGREE_TO_CREATE_NEW_COLLECTION]: (state, { value }) => produce(
    state, draft => {
      if (draft.confirmationModal !== null) {
        // eslint-disable-next-line no-param-reassign
        draft.confirmationModal.wantToCreateNew = value;
      }
    }
  ),
  [REMOVE_UPLOADED_FILE]: state => produce(
    state, draft => {
      if (
        draft.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER
        && draft.createEntitiesModal.data?.type === 'unsuccessful-upload'
      ) {
        // eslint-disable-next-line no-param-reassign
        draft.createEntitiesModal.data = null;
      }
    }
  )
});
